import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import '../styles/TemplateEditSurface.css';

/**
 * Shared prompt-template edit surface: an uncontrolled `contentEditable`
 * kept flat (text nodes + non-editable pill spans carrying `data-field`),
 * extracted from the Map-prompt-template editor so the Model Configuration
 * prompt editors reuse the exact same editing machinery.
 *
 * - Typing serializes the DOM back into the template without re-rendering.
 * - Enter / multi-line paste become template splices (Chrome would otherwise
 *   nest <div> blocks and break every offset computation).
 * - Copy / cut put the selected template substring (pills as `{{name}}`) on
 *   the clipboard, and a paste carrying `{{name}}` is spliced so it renders
 *   as pills again — native copy only sees the pills' text content.
 * - A MutationObserver re-syncs deletions Chrome performs without an `input`
 *   event (Backspace/Delete over a pill, context-menu delete).
 * - Cmd/Ctrl+Z undoes and Cmd/Ctrl+Shift+Z (or Ctrl+Y) redoes template
 *   changes, including deleted pills — native undo is intercepted because it
 *   cannot see through the remounts and splices above.
 * - External template changes (field-panel/rail inserts, unmap, hydration
 *   from the server) are detected and remount the surface from the new value.
 */

/** Typing pauses shorter than this coalesce into one undo step. */
const UNDO_COALESCE_MS = 800;
const UNDO_LIMIT = 200;

/** A `{{name}}` placeholder — pasted text carrying one must render as a pill. */
const TEMPLATE_TOKEN_RE = /\{\{[^}]+\}\}/;

/** Template-string length a DOM node contributes when serialized. */
function nodeTemplateLength(node) {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent.length;
    if (node.nodeType !== Node.ELEMENT_NODE) return 0;
    const field = node.getAttribute && node.getAttribute('data-field');
    if (field) return field.length + 4; // {{name}}
    if (node.tagName === 'BR') return 1;
    return node.textContent.length;
}

/**
 * A <br> in last position is a caret placeholder, not content: pre-wrap
 * alone never renders an empty last line for a trailing '\n', so without it
 * Chrome clamps the caret (and typing) to before that newline. The template
 * already carries the trailing '\n' in the preceding text node, so the
 * placeholder itself serializes to nothing.
 */
function isTrailingBrPlaceholder(root, node) {
    return node.nodeType === Node.ELEMENT_NODE && node.tagName === 'BR' && node === root.lastChild;
}

/** `nodeTemplateLength`, with the trailing-<br> placeholder counting as 0. */
function childTemplateLength(root, node) {
    return isTrailingBrPlaceholder(root, node) ? 0 : nodeTemplateLength(node);
}

/** Editor DOM (flat text nodes + pill spans) back to a template string. */
export function serializeEditorDom(root) {
    let out = '';
    root.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            out += node.textContent;
            return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        const field = node.getAttribute('data-field');
        if (field) out += `{{${field}}}`;
        else if (node.tagName === 'BR') out += isTrailingBrPlaceholder(root, node) ? '' : '\n';
        else out += node.textContent;
    });
    return out;
}

/**
 * Template-string offset of a (container, offset) selection boundary. A
 * boundary inside a pill snaps to after it — or before it with `snapBefore`,
 * so a selection that starts inside a pill still covers the whole pill.
 */
export function templateOffsetAtPoint(root, container, offsetInContainer, { snapBefore = false } = {}) {
    if (container === root) {
        let acc = 0;
        const children = Array.from(root.childNodes).slice(0, offsetInContainer);
        children.forEach((node) => {
            acc += childTemplateLength(root, node);
        });
        return acc;
    }
    let acc = 0;
    for (const node of root.childNodes) {
        if (node === container || (node.contains && node.contains(container))) {
            if (node.nodeType === Node.TEXT_NODE) return acc + offsetInContainer;
            // Boundary inside a pill (or wrapper) — snap to one of its edges.
            return snapBefore ? acc : acc + childTemplateLength(root, node);
        }
        acc += childTemplateLength(root, node);
    }
    return acc;
}

/**
 * Template-string span `{ start, end }` a DOM range covers. A pill the range
 * touches counts whole; a collapsed caret keeps the plain snap-after offset.
 */
export function templateRangeOfDomRange(root, range) {
    const end = templateOffsetAtPoint(root, range.endContainer, range.endOffset);
    if (range.collapsed) return { start: end, end };
    const start = templateOffsetAtPoint(root, range.startContainer, range.startOffset, {
        snapBefore: true,
    });
    return { start: Math.min(start, end), end };
}

/** Put the caret at a template-string offset in the (flat) editor DOM. */
export function setCaretAtTemplateOffset(root, offset) {
    const selection = window.getSelection();
    if (!selection) return;
    const range = document.createRange();
    let acc = 0;
    let placed = false;
    for (const node of root.childNodes) {
        const len = childTemplateLength(root, node);
        if (offset <= acc + len) {
            if (node.nodeType === Node.TEXT_NODE) {
                range.setStart(node, Math.min(offset - acc, node.textContent.length));
            } else {
                // Offsets inside a pill snap to after it.
                range.setStartAfter(node);
            }
            placed = true;
            break;
        }
        acc += len;
    }
    if (!placed) {
        range.selectNodeContents(root);
    }
    range.collapse(placed);
    selection.removeAllRanges();
    selection.addRange(range);
}

/** Caret position where two versions of a template diverge (end of change). */
function divergenceCaret(fromValue, toValue) {
    const max = Math.min(fromValue.length, toValue.length);
    let prefix = 0;
    while (prefix < max && fromValue[prefix] === toValue[prefix]) prefix += 1;
    let suffix = 0;
    while (
        suffix < max - prefix &&
        fromValue[fromValue.length - 1 - suffix] === toValue[toValue.length - 1 - suffix]
    ) {
        suffix += 1;
    }
    return toValue.length - suffix;
}

const TemplateEditSurface = ({
    /** Current template string (controlled by the parent). */
    template,
    /**
     * External remount counter: bump it for mutations that change how pills
     * render without changing the template (e.g. primary-field change).
     * Template-value changes from outside are detected automatically.
     */
    renderVersion = 0,
    /** False locks the surface (viewer role, shuffle in flight). */
    editable = true,
    className = '',
    style,
    ariaLabel,
    ariaInvalid = false,
    /** Shown (via CSS) while the template is empty. */
    placeholder,
    /**
     * `(template) => ReactNode[]` — flat children matching the template:
     * plain text as text nodes / fragments, each `{{name}}` as a
     * `contentEditable={false}` span carrying `data-field={name}`. The
     * trailing-<br> caret placeholder is appended here. Recomputed only on
     * remounts and when `childrenDeps` change — never while the user types.
     */
    renderChildren,
    /** Extra deps that must recompute the pills (e.g. primary field). */
    childrenDeps = [],
    /** Focus the surface on mount and after every remount (modal editors). */
    autoFocus = false,
    onTemplateChange,
    /** Fired with the caret's template-string offset on every caret move. */
    onCaretChange,
    /** Fired with `{ text, start, end, rect }` on a text selection, or null. */
    onSelectText,
    /** Fired with `{ triggerLength, offset, rect }` when `{{` or `/` is typed, or null. */
    onInsertTrigger,
    onMouseUp,
}) => {
    const editorRef = useRef(null);

    // Bumped by newline / multi-line inserts (Enter, multi-line paste), undo/
    // redo, and detected external template changes: those must remount the
    // edit surface from the template instead of going through execCommand,
    // because Chrome turns inserted newlines into nested <div> blocks — and
    // the flat-DOM offset math (templateOffsetAtPoint, serializeEditorDom)
    // silently breaks for everything typed after that.
    const [localVersion, setLocalVersion] = useState(0);
    // Template offset to restore the caret to after a local remount.
    const pendingCaretRef = useRef(null);

    // Capture the template in a ref so the memoized children only recompute
    // on version bumps — never while the user types.
    const templateRef = useRef(template);
    templateRef.current = template;

    const renderChildrenRef = useRef(renderChildren);
    renderChildrenRef.current = renderChildren;

    const children = useMemo(() => {
        const rendered = renderChildrenRef.current(templateRef.current) || [];
        const nodes = Array.isArray(rendered) ? [...rendered] : [rendered];
        // See isTrailingBrPlaceholder: without this, the caret can't reach
        // the empty last line a trailing '\n' creates.
        if (templateRef.current.endsWith('\n')) nodes.push(<br key="trailing-br" />);
        return nodes;
        // Intentionally excludes `template`: recompute only on version bumps
        // so typing never triggers React reconciliation of the edited DOM.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [renderVersion, localVersion, ...childrenDeps]);

    // ---- External-change detection --------------------------------------
    // The template the surface itself last produced (typing, splices,
    // observer sync, undo/redo). A differing prop arrived from outside
    // (panel/rail insert, unmap, hydration) — remount from the new value.
    const selfTemplateRef = useRef(template);
    const prevRenderVersionRef = useRef(renderVersion);
    useEffect(() => {
        const renderVersionChanged = prevRenderVersionRef.current !== renderVersion;
        prevRenderVersionRef.current = renderVersion;
        if (template === selfTemplateRef.current) return;
        selfTemplateRef.current = template;
        // A renderVersion bump already remounts through the key — don't
        // double-remount (it would reset the caret a second time).
        if (!renderVersionChanged) setLocalVersion((version) => version + 1);
    }, [template, renderVersion]);

    // ---- Undo / redo history --------------------------------------------
    // Snapshots of the template; index points at the current state. Typing
    // bursts coalesce into one entry; Enter, paste, and external changes
    // each get their own. Native undo is useless here (it cannot see through
    // remounts and template splices), so Cmd/Ctrl+Z is intercepted.
    const historyRef = useRef([{ value: template, caret: null }]);
    const historyIndexRef = useRef(0);
    const lastRecordTimeRef = useRef(0);
    /** Set by the typing/deletion paths: the next record may coalesce. */
    const coalesceNextRef = useRef(false);
    /** Caret after the latest local edit — stored with its history entry. */
    const lastEditCaretRef = useRef(null);
    const hasUserEditedRef = useRef(false);

    useEffect(() => {
        const history = historyRef.current;
        const index = historyIndexRef.current;
        if (history[index].value === template) return;
        // Pre-edit hydration (e.g. '' -> saved prompt on page load) replaces
        // the baseline instead of becoming an undo step back to empty.
        if (!hasUserEditedRef.current && history.length === 1 && history[0].value === '') {
            historyRef.current = [{ value: template, caret: null }];
            lastRecordTimeRef.current = 0;
            return;
        }
        const now = Date.now();
        const entry = { value: template, caret: lastEditCaretRef.current };
        lastEditCaretRef.current = null;
        const coalesce =
            coalesceNextRef.current &&
            index > 0 &&
            now - lastRecordTimeRef.current < UNDO_COALESCE_MS;
        coalesceNextRef.current = false;
        history.splice(index + 1); // any redo tail dies on a new change
        if (coalesce) {
            history[index] = entry;
        } else {
            history.push(entry);
            historyIndexRef.current += 1;
            if (history.length > UNDO_LIMIT) {
                history.shift();
                historyIndexRef.current -= 1;
            }
        }
        lastRecordTimeRef.current = now;
    }, [template]);

    const restoreHistoryEntry = useCallback(
        (entry, fromValue) => {
            // The next change after an undo/redo must never coalesce into the
            // restored entry — it would overwrite the state just restored.
            lastRecordTimeRef.current = 0;
            coalesceNextRef.current = false;
            const caret = entry.caret ?? divergenceCaret(fromValue, entry.value);
            selfTemplateRef.current = entry.value;
            pendingCaretRef.current = caret;
            onTemplateChange?.(entry.value);
            onSelectText?.(null);
            onInsertTrigger?.(null);
            setLocalVersion((version) => version + 1);
        },
        [onTemplateChange, onSelectText, onInsertTrigger]
    );

    const undo = useCallback(() => {
        const history = historyRef.current;
        const index = historyIndexRef.current;
        if (index === 0) return;
        historyIndexRef.current = index - 1;
        restoreHistoryEntry(history[index - 1], history[index].value);
    }, [restoreHistoryEntry]);

    const redo = useCallback(() => {
        const history = historyRef.current;
        const index = historyIndexRef.current;
        if (index >= history.length - 1) return;
        historyIndexRef.current = index + 1;
        restoreHistoryEntry(history[index + 1], history[index].value);
    }, [restoreHistoryEntry]);

    // ---- Editing handlers ------------------------------------------------

    const caretRect = useCallback(() => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return null;
        const range = selection.getRangeAt(0).cloneRange();
        range.collapse(true);
        const rects = range.getClientRects();
        return rects.length > 0 ? rects[0] : range.getBoundingClientRect();
    }, []);

    const caretTemplateOffset = useCallback(() => {
        const root = editorRef.current;
        const selection = window.getSelection();
        if (!root || !selection || selection.rangeCount === 0) return null;
        const range = selection.getRangeAt(0);
        if (!root.contains(range.startContainer)) return null;
        return templateOffsetAtPoint(root, range.endContainer, range.endOffset);
    }, []);

    const handleInput = useCallback(() => {
        const root = editorRef.current;
        if (!root) return;
        const next = serializeEditorDom(root);
        selfTemplateRef.current = next;
        hasUserEditedRef.current = true;
        coalesceNextRef.current = true;
        onTemplateChange?.(next);

        const offset = caretTemplateOffset();
        lastEditCaretRef.current = offset;
        if (offset === null) {
            onInsertTrigger?.(null);
            return;
        }
        onCaretChange?.(offset);
        // Typing destroys the highlight a selection menu was opened for —
        // close it so a later pick can't use stale offsets.
        onSelectText?.(null);
        const before = next.substring(0, offset);
        if (before.endsWith('{{')) {
            onInsertTrigger?.({ triggerLength: 2, offset, rect: caretRect() });
        } else if (/(^|\s)\/$/.test(before)) {
            onInsertTrigger?.({ triggerLength: 1, offset, rect: caretRect() });
        } else {
            onInsertTrigger?.(null);
        }
    }, [
        onTemplateChange,
        onCaretChange,
        onInsertTrigger,
        onSelectText,
        caretTemplateOffset,
        caretRect,
    ]);

    // Chrome doesn't reliably fire an `input` event when a native editing
    // operation consumes a non-editable pill (Backspace/Delete over a
    // selection spanning one, context-menu delete): the pill leaves the
    // DOM but handleInput never runs, so the template state — and anything
    // derived from it — silently keeps the field. Watch the surface and
    // re-serialize any mutation the input event missed; when the input path
    // already ran, the serialized string matches the template and this is a
    // no-op.
    useEffect(() => {
        const root = editorRef.current;
        if (!root) return undefined;
        const observer = new MutationObserver(() => {
            const next = serializeEditorDom(root);
            if (next === templateRef.current) return;
            selfTemplateRef.current = next;
            hasUserEditedRef.current = true;
            coalesceNextRef.current = true;
            onTemplateChange?.(next);
            // The caret collapsed at the deletion point; without this a
            // later panel-click insert would use the pre-delete offset.
            const offset = caretTemplateOffset();
            lastEditCaretRef.current = offset;
            if (offset !== null) onCaretChange?.(offset);
        });
        observer.observe(root, { childList: true, subtree: true, characterData: true });
        return () => observer.disconnect();
        // renderVersion / localVersion remount the surface (key change), so
        // the observer must re-attach to the fresh element.
    }, [renderVersion, localVersion, onTemplateChange, onCaretChange, caretTemplateOffset]);

    /**
     * Replace the template span `[start, end)` with `text` and remount the
     * edit surface with the caret after the inserted text.
     *
     * Newlines must never go through `execCommand('insertText')`: Chrome
     * represents them as nested <div> blocks, which breaks the flat-DOM
     * invariant every offset computation relies on (typing inside such a
     * block snaps the caret offset to after the whole block, so the `{{` /
     * `/` menu can never open again, and template splices land wrong).
     */
    const spliceTemplate = useCallback(
        (start, end, text) => {
            const current = templateRef.current;
            const next = `${current.substring(0, start)}${text}${current.substring(end)}`;
            selfTemplateRef.current = next;
            hasUserEditedRef.current = true;
            // Enter / paste / cut are discrete actions: each gets its own undo step.
            coalesceNextRef.current = false;
            onTemplateChange?.(next);
            const caret = start + text.length;
            pendingCaretRef.current = caret;
            lastEditCaretRef.current = caret;
            onCaretChange?.(caret);
            onSelectText?.(null);
            onInsertTrigger?.(null);
            setLocalVersion((version) => version + 1);
        },
        [onTemplateChange, onCaretChange, onSelectText, onInsertTrigger]
    );

    /** Template span the current selection covers, or null when it lies outside the surface. */
    const selectedTemplateRange = useCallback(() => {
        const root = editorRef.current;
        const selection = window.getSelection();
        if (!root || !selection || selection.rangeCount === 0) return null;
        const range = selection.getRangeAt(0);
        if (!root.contains(range.commonAncestorContainer)) return null;
        return templateRangeOfDomRange(root, range);
    }, []);

    /** Insert text at the selection by splicing the template (see spliceTemplate). */
    const insertTextAsTemplateSplice = useCallback(
        (text) => {
            const selected = selectedTemplateRange();
            if (!selected) return;
            spliceTemplate(selected.start, selected.end, text);
        },
        [selectedTemplateRange, spliceTemplate]
    );

    const handlePaste = useCallback(
        (event) => {
            event.preventDefault();
            const text = event.clipboardData?.getData('text/plain');
            if (!text) return;
            if (text.includes('\n') || TEMPLATE_TOKEN_RE.test(text)) {
                // Multi-line paste has the same nested-<div> problem as Enter.
                // A `{{name}}` paste (e.g. content copied from this surface)
                // must remount too: execCommand would insert it as raw text,
                // and the pills only come from renderChildren.
                insertTextAsTemplateSplice(text);
            } else {
                document.execCommand('insertText', false, text);
            }
        },
        [insertTextAsTemplateSplice]
    );

    // Native copy serializes the selection's text content: pills lose their
    // `{{ }}` and — being inline-flex — gain line breaks around them, so what
    // came back on paste was plain text on separate lines. Put the exact
    // template substring on the clipboard instead; pasting it here restores
    // the pills, and anywhere else yields the template as written.
    const writeSelectionToClipboard = useCallback(
        (event) => {
            if (!event.clipboardData) return null;
            const selected = selectedTemplateRange();
            if (!selected || selected.start === selected.end) return null;
            event.preventDefault();
            event.clipboardData.setData(
                'text/plain',
                templateRef.current.substring(selected.start, selected.end)
            );
            return selected;
        },
        [selectedTemplateRange]
    );

    const handleCopy = useCallback(
        (event) => {
            writeSelectionToClipboard(event);
        },
        [writeSelectionToClipboard]
    );

    // Cut deletes through a template splice rather than the native edit (which
    // the MutationObserver would only re-sync after the fact): the removal is
    // its own undo step and the caret lands at the cut point. A locked surface
    // still copies — that is all a native cut would do to it.
    const handleCut = useCallback(
        (event) => {
            const selected = writeSelectionToClipboard(event);
            if (!selected || !editable) return;
            spliceTemplate(selected.start, selected.end, '');
        },
        [writeSelectionToClipboard, editable, spliceTemplate]
    );

    const reportSelection = useCallback(() => {
        const root = editorRef.current;
        const selection = window.getSelection();
        if (!root || !selection || selection.rangeCount === 0) return;
        const range = selection.getRangeAt(0);
        if (!root.contains(range.commonAncestorContainer)) return;

        const end = templateOffsetAtPoint(root, range.endContainer, range.endOffset);
        onCaretChange?.(end);

        const text = selection.toString();
        if (range.collapsed || !text.trim()) {
            onSelectText?.(null);
            return;
        }
        // Selections overlapping a pill can't become a custom field.
        const pills = root.querySelectorAll('[data-field]');
        for (const pill of pills) {
            if (range.intersectsNode(pill)) {
                onSelectText?.(null);
                return;
            }
        }
        const start = templateOffsetAtPoint(root, range.startContainer, range.startOffset);
        // Anchor the popover to the selection's last line (near the mouse
        // release), not the whole bounding box — a multi-line selection's box
        // spans most of the editor and would push the menu out of view.
        const rects = range.getClientRects();
        const rect = rects.length > 0 ? rects[rects.length - 1] : range.getBoundingClientRect();
        onSelectText?.({ text, start, end, rect });
    }, [onCaretChange, onSelectText]);

    // A drag that starts in the editor often ends outside it (selecting "the
    // rest of the text" auto-scrolls and the pointer leaves the box), so the
    // element's own mouseup never fires. Catch the release at the document
    // level, but only for gestures that started inside the editor — this is
    // what makes highlighting open the fields menu as reliably as typing {{.
    const selectingRef = useRef(false);
    const markSelecting = useCallback(() => {
        selectingRef.current = true;
    }, []);
    useEffect(() => {
        const onDocumentMouseUp = () => {
            if (!selectingRef.current) return;
            selectingRef.current = false;
            reportSelection();
        };
        document.addEventListener('mouseup', onDocumentMouseUp);
        return () => document.removeEventListener('mouseup', onDocumentMouseUp);
    }, [reportSelection]);

    // Keep the DOM flat: Enter becomes a literal newline instead of a <div>.
    // Except over a highlight with a selection consumer: Enter then opens the
    // fields menu, same as releasing a mouse selection.
    const handleKeyDown = useCallback(
        (event) => {
            const key = event.key.toLowerCase();
            const mod = event.metaKey || event.ctrlKey;
            if (mod && !event.altKey && key === 'z') {
                event.preventDefault();
                if (event.shiftKey) redo();
                else undo();
                return;
            }
            if (mod && !event.altKey && !event.shiftKey && key === 'y') {
                event.preventDefault();
                redo();
                return;
            }
            if (event.key !== 'Enter') return;
            event.preventDefault();
            const root = editorRef.current;
            const selection = window.getSelection();
            const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
            const hasHighlight =
                root &&
                range &&
                !range.collapsed &&
                selection.toString().trim() &&
                root.contains(range.commonAncestorContainer);
            if (hasHighlight && onSelectText) {
                reportSelection();
                return;
            }
            insertTextAsTemplateSplice('\n');
        },
        [undo, redo, onSelectText, reportSelection, insertTextAsTemplateSplice]
    );

    // Keyboard selection (Shift/Cmd/Option/Ctrl + arrows, Home/End) must never
    // OPEN the selection menu mid-gesture — mounting it would steal focus and
    // kill the native selection. Keyup only tracks the caret and closes a
    // stale menu once the selection collapses; opening happens on mouse
    // release or on Enter over the highlight (handleKeyDown above).
    const reportCaretFromKeyboard = useCallback(() => {
        const root = editorRef.current;
        const selection = window.getSelection();
        if (!root || !selection || selection.rangeCount === 0) return;
        const range = selection.getRangeAt(0);
        if (!root.contains(range.commonAncestorContainer)) return;

        onCaretChange?.(templateOffsetAtPoint(root, range.endContainer, range.endOffset));
        if (range.collapsed || !selection.toString().trim()) {
            onSelectText?.(null);
        }
    }, [onCaretChange, onSelectText]);

    // Remounts recreate the element scrolled to the top. Track the last
    // scroll position and restore it before paint so inserting a field (menu
    // pick, panel/rail insert) doesn't jump the editor back to the top.
    const scrollTopRef = useRef(0);
    const handleScroll = useCallback(() => {
        scrollTopRef.current = editorRef.current?.scrollTop ?? 0;
    }, []);
    useLayoutEffect(() => {
        const root = editorRef.current;
        if (root) root.scrollTop = scrollTopRef.current;
    }, [renderVersion, localVersion]);

    // Refocus after remounts so the user can keep typing. Modal editors
    // (autoFocus) focus on every remount including mount; inline editors only
    // when a local edit (Enter, paste, undo/redo) queued a caret restore —
    // never on mount or external changes, which would steal page focus.
    useEffect(() => {
        const root = editorRef.current;
        if (!root) return;
        if (!autoFocus && pendingCaretRef.current === null) return;
        root.focus({ preventScroll: true });
        if (pendingCaretRef.current !== null) {
            setCaretAtTemplateOffset(root, pendingCaretRef.current);
            pendingCaretRef.current = null;
        }
    }, [renderVersion, localVersion, autoFocus]);

    const rootClassName = [
        'template-edit-surface',
        template === '' ? 'template-edit-surface--empty' : '',
        className,
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div
            key={`${renderVersion}:${localVersion}`}
            ref={editorRef}
            className={rootClassName}
            style={style}
            contentEditable={editable}
            suppressContentEditableWarning
            spellCheck={false}
            role="textbox"
            aria-multiline="true"
            aria-label={ariaLabel}
            aria-readonly={!editable || undefined}
            aria-invalid={ariaInvalid || undefined}
            data-placeholder={placeholder || undefined}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onCopy={handleCopy}
            onCut={handleCut}
            onMouseDown={markSelecting}
            onKeyUp={reportCaretFromKeyboard}
            onScroll={handleScroll}
            onMouseUp={onMouseUp}
        >
            {children}
        </div>
    );
};

export default TemplateEditSurface;
